import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Radio } from 'lucide-react';

// Status translations
const statusTranslations = {
  'available': 'DISPONIBLE',
  'coming_soon': 'PRÓXIMAMENTE',
  'exclusive': 'EXCLUSIVO',
  'new': 'NUEVO',
  'archived': 'ARCHIVADO'
};

// Category display names (for badges in Spanish)
const CATEGORY_LABELS = {
  'ÒPERA': 'Ópera',
  'BALLET': 'Ballet',
  'CONCERT': 'Concierto',
  'DOCUMENTAL': 'Documental',
  'PELÍCULA': 'Película'
};

// Helper function to get category label
const getCategoryLabel = (category) => CATEGORY_LABELS[category] || category;

// Format date to dd/mm/yyyy
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    // Handle both ISO date and yyyy-mm-dd formats
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return dateStr;
  }
};

// Format time to HH:MM
const formatTime = (timeStr) => {
  if (!timeStr) return '';
  return timeStr;
};

const OperaCard = ({ opera, variant = 'default' }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (opera.id) {
      navigate(`/content/${opera.id}`);
    }
  };

  // Get category for the badge (always show category, not section name for live events)
  const badgeText = opera.category || opera.sectionName || '';
  
  // Translate status to Spanish
  const displayStatus = statusTranslations[opera.status?.toLowerCase()] || opera.status?.toUpperCase() || 'DISPONIBLE';
  
  // Format date
  const displayDate = formatDate(opera.date);
  
  // Check if this is a live event
  const isLive = opera.isLive || opera.is_live;
  const liveDate = formatDate(opera.liveDate || opera.live_date);
  const liveTime = formatTime(opera.liveTime || opera.live_time);
  
  // Check if this is an encore event
  const isEncore = opera.isEncore || opera.is_encore;
  const encoreStartDate = formatDate(opera.encoreStartDate || opera.encore_start_date);
  const encoreEndDate = formatDate(opera.encoreEndDate || opera.encore_end_date);

  return (
    <div 
      onClick={handleClick}
      className="group relative flex-shrink-0 w-[280px] md:w-[320px] cursor-pointer"
    >
      {/* Card Container */}
      <div className="relative h-[420px] md:h-[480px] rounded-lg overflow-hidden bg-[#1a1a1a]">
        {/* Image */}
        <div className="absolute inset-0">
          <img
            src={opera.image}
            alt={opera.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
        </div>

        {/* Category Badge - Top Right (shows category like Ballet, Ópera) */}
        {badgeText && (
          <div className="absolute top-3 right-3">
            <span className="px-2 py-1 bg-black/60 backdrop-blur-sm text-white/80 text-xs font-medium rounded">
              {getCategoryLabel(badgeText)}
            </span>
          </div>
        )}

        {/* Live indicator - Small red dot at top left for live events */}
        {isLive && (
          <div className="absolute top-3 left-3 z-10">
            <span className="flex items-center gap-1.5 px-2 py-1 bg-[#e4002b] text-white text-xs font-bold rounded-full">
              <Radio className="w-3 h-3 animate-pulse" />
            </span>
          </div>
        )}
        
        {/* Encore indicator - Amber badge at top left for encore events */}
        {isEncore && !isLive && (
          <div className="absolute top-3 left-3 z-10">
            <span className="px-2 py-1 bg-amber-500 text-black text-xs font-bold rounded-full">
              ENCORE
            </span>
          </div>
        )}

        {/* Content */}
        <div className="absolute bottom-0 left-0 right-0 p-5">
          <h3 className="text-white text-xl md:text-2xl font-semibold mb-1 group-hover:text-[#e4002b] transition-colors">
            {opera.title}
          </h3>
          <p className="text-[#e4002b] text-sm font-medium tracking-wide uppercase mb-1">
            {opera.composer}
          </p>
          
          {/* Theater/Artists */}
          {opera.artists && (
            <p className="text-white/50 text-xs mb-2">
              {opera.artists}
            </p>
          )}
          
          {/* Live Event: Date and Time between theater and status */}
          {isLive && (liveDate || liveTime) && (
            <p className="text-white/70 text-xs mb-1">
              <span className="text-[#e4002b] font-medium">{liveDate}</span>
              {liveDate && liveTime && ' · '}
              {liveTime && <span className="text-white font-semibold">{liveTime}h</span>}
            </p>
          )}
          
          {/* Encore Event: Show available date range */}
          {isEncore && (encoreStartDate || encoreEndDate) && (
            <p className="text-amber-400 text-xs mb-1">
              Disponible: {encoreStartDate} - {encoreEndDate}
            </p>
          )}
          
          {/* Status - Shows "DIRECTO" for live, "ENCORE" for encore, otherwise normal status */}
          {isLive ? (
            <p className="text-white/70 text-xs mb-4 uppercase flex items-center gap-1">
              <Radio className="w-3 h-3 text-[#e4002b]" />
              DIRECTO
            </p>
          ) : isEncore ? (
            <p className="text-amber-400 text-xs mb-4 uppercase font-medium">
              ENCORE
            </p>
          ) : (
            <>
              {/* Regular premiere info */}
              {displayDate && (
                <p className="text-white/70 text-xs mb-1">
                  {opera.releaseLabel || 'ESTRENO DIGITAL'} <span className="text-[#e4002b]">{displayDate}</span>
                </p>
              )}
              <p className="text-white/70 text-xs mb-4 uppercase">
                {displayStatus}
              </p>
            </>
          )}

          {/* CTA Button */}
          <button 
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/content/${opera.id}`);
            }}
            className="px-6 py-2.5 bg-[#e4002b] text-white text-xs font-medium tracking-wide rounded hover:bg-[#c70024] transition-colors"
          >
            {opera.cta || 'CONTRATAR'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OperaCard;
